import ApiBase from './ApiBase';
import LogApi from './LogApi';
import { getIcError } from '../../core/providers/utils';
import axios from 'axios';
import { stringify } from '../components/forked/stringify';
import { v4 as uuidv4 } from 'uuid';

/**
 * Resource upload API
 */
class ResourceUploadApi extends ApiBase {
  /**
   * Upload batch
   * @param {*} formData Form Data
   * @param progress
   */
  async upload(formData, progress) {
    // Get headers for form-data upload
    const headers = this.getHeaders();
    delete headers['Content-Type'];
    headers.Accept = 'application/json';

    const request = {
      url: `${this.API_URL}/resourceupload`,
      method: 'POST',
      headers: headers,
      onUploadProgress: progressEvent => {
        if (progressEvent.lengthComputable) {
          if (typeof progress === 'function') {
            progress(progressEvent.loaded, progressEvent.total);
          }
        }
      },
      data: formData
    };

    const response = await axios.request(request);

    // Handle Gateway Timeout
    if (response.status === 504) {
      throw new Error('The request took too long and timed out.');
    }

    if (response.status !== 201) {
      throw new Error(response.statusText);
    }
    return response.data;
  }

  /**
   * Update batch
   * @param {*} formData
   * @param progress
   */
  async updateResourceUpload(formData, progress) {
    // Get headers for form-data upload
    const headers = this.getHeaders();
    delete headers['Content-Type'];
    headers.Accept = 'application/json';

    const request = {
      url: `${this.API_URL}/resourceupload`,
      headers: headers,
      method: 'PUT',
      data: formData,
      onUploadProgress: progressEvent => {
        if (progressEvent.lengthComputable) {
          if (typeof progress === 'function') {
            progress(progressEvent.loaded, progressEvent.total);
          }
        }
      }
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Download uploaded file
   * @param {*} resourceUploadId
   * @param progress
   */
  async download(resourceUploadId, progress) {
    const request = {
      url: `${this.API_URL}/resourceupload/download?resourceUploadId=${resourceUploadId}`,
      headers: this.getHeaders(),
      method: 'GET',
      responseType: 'blob',
      onDownloadProgress: progressEvent => {
        if (progressEvent.lengthComputable) {
          if (typeof progress === 'function') {
            progress(progressEvent.loaded, progressEvent.total);
          }
        }
      }
    };

    const response = await axios.request(request);

    // Handle Gateway Timeout
    if (response.status === 504) {
      throw new Error('The request took too long and timed out.');
    }

    return response;
  }

  /**
   * Start split of bulk upload
   * @param {*} resourceUploadId
   * @param {*} splitMethodId
   */
  async split(resourceUploadId, splitMethodId) {
    const request = {
      url: `${this.API_URL}/resourceupload/split?splitMethodId=${splitMethodId}&resourceUploadId=${resourceUploadId}`,
      headers: this.getHeaders(),
      method: 'POST'
    };

    // Start split
    const response = await axios.request(request);

    if (response.status === 202) {
    }
  }

  /**
   * Get split job status
   * @param {*} resourceUploadId
   */
  async getSplitStatus(resourceUploadId) {
    const request = {
      url: `${
        this.API_URL
      }/resourceupload/split/status?resourceUploadId=${resourceUploadId}&timestamp=${new Date().getTime()}`,
      headers: this.getHeaders(),
      method: 'GET'
    };

    // Start split
    const response = await axios.request(request);

    if (response.status === 404) {
      // Special case : split not started yet
      return;
    }
    return response.data;
  }

  /**
   * Set batch status
   * @param {*} resourceUploadId
   * @param {*} newStatus
   */
  async setBatchStatus(resourceUploadId, newStatus) {
    let setAction = '';
    switch (newStatus) {
      case 'A':
        setAction = 'setactive';
        break;
      case 'P':
        setAction = 'setpending';
        break;
      case 'S':
        setAction = 'setsuspended';
        break;
      default:
        setAction = 'setactive';
        break;
    }
    var params = new URLSearchParams();
    params.append('resourceUploadId', resourceUploadId);

    const request = {
      url: `${this.API_URL}/resourceupload/${setAction}?${params.toString()}`,
      headers: this.getHeaders(),
      method: 'POST'
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Set mulitple batches status to ACTIVE
   * @param data
   */
  async setMultipleActive(data) {
    const headers = this.getHeaders();
    const request = {
      url: `${this.API_URL}/resourceuploads/setactive`,
      headers: headers,
      method: 'POST',
      data: data
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Set mulitple batches status to PENDING
   * @param data
   */
  async setMultiplePending(data) {
    const headers = this.getHeaders();
    const request = {
      url: `${this.API_URL}/resourceuploads/setpending`,
      headers: headers,
      method: 'POST',
      data: data
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Set mulitple batches status to SUSPENDED
   * @param data
   */
  async setMultipleSuspended(data) {
    const headers = this.getHeaders();
    const request = {
      url: `${this.API_URL}/resourceuploads/setsuspended`,
      headers: headers,
      method: 'POST',
      data: data
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Associate fund/investor
   * @param fundInvestors
   */
  async setFundinvestors(fundInvestors) {
    const request = {
      url: `${this.API_URL}/resourcefundinvestors`,
      headers: this.getHeaders(),
      method: 'POST',
      data: JSON.stringify(fundInvestors)
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Get fund/investor
   * @param {*} resourceUploadId
   */
  async getFundinvestors(resourceUploadId) {
    const request = {
      url: `${this.API_URL}/resourcefundinvestors?resourceUploadId=${resourceUploadId}`,
      headers: this.getHeaders(),
      method: 'GET'
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Delete fund/investor
   * @param fundInvestors
   */
  async deleteFundinvestors(fundInvestors) {
    const request = {
      url: `${this.API_URL}/resourcefundinvestors`,
      headers: this.getHeaders(),
      method: 'DELETE',
      data: JSON.stringify(fundInvestors)
    };

    await axios.request(request);
  }

  /**
   * Start proof of bulk upload
   * @param {*} resourceUploadId
   */
  async proof(resourceUploadId) {
    const request = {
      url: `${this.API_URL}/resourceupload/proof?resourceUploadId=${resourceUploadId}`,
      headers: this.getHeaders(),
      method: 'POST'
    };

    const response = await axios.request(request);

    if (response.status === 202) {
    }
  }

  /**
   * Get proof job status
   * @param {*} resourceUploadId
   */
  async getProofStatus(resourceUploadId) {
    const request = {
      url: `${
        this.API_URL
      }/resourceupload/proof/status?resourceUploadId=${resourceUploadId}&timestamp=${new Date().getTime()}`,
      headers: this.getHeaders(),
      method: 'GET'
    };

    const response = await axios.request(request);

    if (response.status === 404) {
      // Special case : proofing not started yet
      return;
    }
    return response.data;
  }

  /**
   * Download proofing file
   * @param {*} resourceUploadId
   * @param progress
   */
  async downloadProofFile(resourceUploadId, progress) {
    const request = {
      url: `${this.API_URL}/resourceupload/proof/download?resourceUploadId=${resourceUploadId}`,
      headers: this.getHeaders(),
      method: 'GET',
      responseType: 'blob',
      onDownloadProgress: progressEvent => {
        if (progressEvent.lengthComputable) {
          if (typeof progress === 'function') {
            progress(progressEvent.loaded, progressEvent.total);
          }
        }
      }
    };

    const response = await axios.request(request);
    return response;
  }

  /**
   * Notify by mail
   * @param {*} resourceUploadId
   */
  async notify(resourceUploadId) {
    const trackingId = uuidv4();

    LogApi.sendClientlogs(
      LogApi.createTrackingObject(
        trackingId,
        null,
        null,
        null,
        null,
        `POST v1/resourceupload/notify`,
        `Investor Notify API call`
      )
    ).catch(err => {
      console.error('Server error: ' + getIcError(err));
    });

    const headers = this.getHeaders();
    headers['TRACKING-REQUEST-ID'] = trackingId;

    const request = {
      method: 'POST',
      url: `${this.API_URL}/resourceupload/notify?resourceUploadId=${resourceUploadId}`,
      headers: headers
    };

    const response = await axios.request(request);

    return response;
  }

  /**
   * Get proof job status
   * @param {*} resourceUploadId
   */
  async getBatchWithDocuments(resourceUploadId) {
    const batchRequest = {
      url: `${this.API_URL}/resourceupload?resourceUploadId=${resourceUploadId}`,
      headers: this.getHeaders(),
      method: 'GET'
    };

    const documentRequest = {
      url: `${this.API_URL}/resourceupload/documents?resourceUploadId=${resourceUploadId}`,
      headers: this.getHeaders(),
      method: 'GET'
    };

    const batchForEdit = await axios
      .all([axios.request(batchRequest), axios.request(documentRequest)])
      .then(
        axios.spread((batchResponse, documentsResponse) => {
          const batch = batchResponse.data;
          const documents = documentsResponse.data;
          return { batch, documents };
        })
      );

    return batchForEdit;
  }

  /**
   * Get recipients excel file by distribution resource upload id
   * @param params
   */
  async getResourceUploadDistributionListExcel(params) {
    const request = {
      url: `${this.API_URL}/resourceupload/distributionlist/excel?${stringify(
        params
      )}`,
      headers: this.getHeaders(),
      responseType: 'blob',
      method: 'GET'
    };

    const response = await axios.request(request);

    // Handle Gateway Timeout
    if (response.status === 504) {
      throw new Error('The request took too long and timed out.');
    }

    return response.data;
  }

  /**
   * Send fundids and excel file, validate content and return
   * fund investor combination with validation message.
   * @param fundIds
   * @param value
   */
  async fundInvestorValidateImport(fundIds, value) {
    const formData = new FormData();

    const fundIdMetaDataDto = {
      fundIds: fundIds
    };

    formData.append('fundIdMetaData', JSON.stringify(fundIdMetaDataDto));
    formData.append('excelFile', value, value.name);
    const request = {
      url: `${this.API_URL}/resourceupload/fundinvestor/validateimport`,
      method: 'POST',
      headers: this.getHeaders(),
      data: formData
    };

    const response = await axios.request(request);

    // Handle Gateway Timeout
    if (response.status === 504) {
      throw new Error('The request took too long and timed out.');
    }

    return response.data;
  }

  async downloadDocumentUploadImportTemplate() {
    const request = {
      url: `${this.API_URL}/resourceupload/fundinvestor/import/template`,
      method: 'GET',
      responseType: 'blob',
      headers: this.getHeaders()
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Change title of the batch
   * @param {*} resourceUploadId
   * @param {*} resourceUploadTitle
   */
  async updateResourceUploadTitle(resourceUploadId, resourceUploadTitle) {
    const request = {
      url: `${this.API_URL}/resourceupload/title?resourceUploadId=${resourceUploadId}`,
      headers: this.getHeaders(),
      method: 'PUT',
      data: resourceUploadTitle
    };
    const response = await axios.request(request);
    return response.data;
  }

  /**
   * Set reviewed status for a batch
   * @param {*} resourceUploadId
   * @param {*} reviewStatus
   */
  async setDocumentBatchReviewedByStatus(resourceUploadId, reviewStatus) {
    // Construct query parameters
    const params = new URLSearchParams();
    params.append('resourceUploadId', resourceUploadId);
    params.append('reviewStatus', reviewStatus);

    // Prepare request
    const request = {
      url: `${this.API_URL}/resourceupload/setreview?${params.toString()}`,
      headers: this.getHeaders(),
      method: 'POST'
    };

    // Send request
    const response = await axios.request(request);
    return response.data;
  }

  /**
   * Update email notify flag for a batch
   * @param {*} resourceUploadId
   * @param {*} emailNotifyFlag
   */
  async updateEmailNotifyFlag(resourceUploadId, emailNotifyFlag) {
    const params = new URLSearchParams();
    params.append('resourceUploadId', resourceUploadId);
    params.append('emailNotifyFlag', emailNotifyFlag);

    const request = {
      url: `${
        this.API_URL
      }/resourceupload/emailnotifyflag?${params.toString()}`,
      headers: this.getHeaders(),
      method: 'PUT'
    };

    const response = await axios.request(request);
    return response.data;
  }
}
export default new ResourceUploadApi();
